(function () {
  if (window.customElements && customElements.get('ck-product-upsell')) return;

  class CkProductUpsell extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.carousel = this.querySelector('[data-ck-carousel]');
      this.errorString = this.dataset.strError || 'Something went wrong. Please try again.';
      this.productCache = {};
      this.flickity = null;
      this.resizeRaf = null;
      this.lastEqualizedHeight = 0;

      this.onClick = this.onClick.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);

      this.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onWindowResize);

      this.initCarousel();
      this.scheduleEqualHeights();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onWindowResize);
      if (this.flickity) {
        try { this.flickity.destroy(); } catch (e) { /* noop */ }
      }
    }

    initCarousel() {
      if (!this.carousel || typeof window.Flickity === 'undefined') return;
      const slides = this.carousel.querySelectorAll('[data-ck-slide]');
      if (!slides.length) return;

      const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

      try {
        this.flickity = new window.Flickity(this.carousel, {
          cellSelector: '[data-ck-slide]',
          cellAlign: 'left',
          prevNextButtons: slides.length > 2,
          pageDots: false,
          wrapAround: false,
          contain: true,
          groupCells: isMobile ? 1 : 2,
          friction: 0.8,
          selectedAttraction: 0.14,
          rightToLeft: !!(window.theme && window.theme.config && window.theme.config.rtl)
        });

        this.flickity.on('ready', () => this.scheduleEqualHeights());
        this.flickity.on('settle', () => this.scheduleEqualHeights());
      } catch (e) {
        console.warn('[ck-product-upsell] Flickity init failed', e);
      }

      const images = this.carousel.querySelectorAll('img');
      images.forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => this.scheduleEqualHeights(), { once: true });
        img.addEventListener('error', () => this.scheduleEqualHeights(), { once: true });
      });
    }

    onClick(event) {
      const inlineClose = event.target.closest('[data-ck-inline-close]');
      if (inlineClose) {
        const card = inlineClose.closest('.ck-product-upsell__card');
        if (card) this.closeInlinePicker(card);
        return;
      }

      const inlineVariantBtn = event.target.closest('[data-ck-inline-variant]');
      if (inlineVariantBtn && this.contains(inlineVariantBtn)) {
        this.addInlineVariant(inlineVariantBtn);
        return;
      }

      const toggle = event.target.closest('[data-ck-toggle]');
      if (!toggle || !this.contains(toggle)) return;

      if (toggle.dataset.hasVariants === 'true') {
        this.openInlinePicker(toggle);
        return;
      }

      this.addSingleVariant(toggle);
    }

    addSingleVariant(toggleBtn) {
      const productId = toggleBtn.dataset.productId;
      const variantId = toggleBtn.dataset.variantId;
      if (!productId || !variantId) return;

      this.addToCart(variantId, toggleBtn);
    }

    openInlinePicker(toggleBtn) {
      const handle = toggleBtn.dataset.productHandle;
      const card = toggleBtn.closest('.ck-product-upsell__card');
      if (!handle || !card) return;

      const optionsEl = card.querySelector('[data-ck-inline-options]');
      if (!optionsEl) return;

      this.querySelectorAll('.ck-product-upsell__card.is-picker-open').forEach((el) => {
        if (el !== card) this.closeInlinePicker(el);
      });

      card.classList.add('is-picker-open');
      optionsEl.innerHTML = '<p class="ck-product-upsell__inline-loading">Loading...</p>';

      if (this.productCache[handle]) {
        this.renderInlineOptions(card, this.productCache[handle]);
        return;
      }

      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('Failed loading product');
          return res.json();
        })
        .then((product) => {
          this.productCache[handle] = product;
          this.renderInlineOptions(card, product);
        })
        .catch(() => {
          optionsEl.innerHTML = '<p class="ck-product-upsell__inline-loading">Unable to load variants.</p>';
        });
    }

    renderInlineOptions(card, product) {
      if (!product || !Array.isArray(product.variants)) return;

      const optionsEl = card.querySelector('[data-ck-inline-options]');
      const rows = product.variants
        .filter((variant) => variant && variant.available)
        .map((variant) => {
          const title = variant.title && variant.title !== 'Default Title' ? variant.title : 'Default';
          return (
            '<button type="button" class="ck-product-upsell__inline-option" data-ck-inline-variant data-product-id="' + product.id + '" data-variant-id="' + variant.id + '" aria-pressed="false">' +
              '<span>' + this.escapeHtml(title) + '</span>' +
            '</button>'
          );
        });

      optionsEl.innerHTML = rows.length ? rows.join('') : '<p class="ck-product-upsell__inline-loading">No available variants.</p>';
    }

    addInlineVariant(btn) {
      const productId = btn.dataset.productId;
      const variantId = btn.dataset.variantId;
      if (!productId || !variantId) return;
      const toggleBtn = this.querySelector('[data-ck-toggle][data-product-id="' + productId + '"]');
      this.addToCart(variantId, toggleBtn || btn)
        .then(() => {
          const card = btn.closest('.ck-product-upsell__card');
          if (card) this.closeInlinePicker(card);
        })
        .catch(() => {});
    }

    closeInlinePicker(card) {
      if (!card) return;
      card.classList.remove('is-picker-open');
    }

    onWindowResize() {
      this.scheduleEqualHeights();
    }

    scheduleEqualHeights() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.equalizeCardHeights();
      });
    }

    equalizeCardHeights() {
      if (!this.carousel) return;
      const cards = this.carousel.querySelectorAll('.ck-product-upsell__card');
      if (!cards.length) return;

      cards.forEach((card) => { card.style.height = ''; });

      let max = 0;
      cards.forEach((card) => {
        const h = card.offsetHeight;
        if (h > max) max = h;
      });

      if (!max) return;
      const targetHeight = max + 'px';
      let needsApply = false;
      cards.forEach((card) => {
        if (card.style.height !== targetHeight) needsApply = true;
      });

      if (needsApply) {
        cards.forEach((card) => { card.style.height = targetHeight; });
      }

      // Trigger Flickity layout only when the target max changed.
      if (this.flickity && this.lastEqualizedHeight !== max) {
        this.flickity.resize();
      }
      this.lastEqualizedHeight = max;
    }

    addToCart(variantId, btn) {
      this.setBusy(btn, true);

      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => { throw new Error((err && err.description) || res.statusText); });
          }
          return res.json();
        })
        .then(() => {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          if (btn) {
            btn.classList.add('is-selected');
            setTimeout(() => btn.classList.remove('is-selected'), 900);
          }
        })
        .catch((err) => {
          console.error('[ck-product-upsell] add to cart failed', err);
          this.announce(this.errorString);
          throw err;
        })
        .finally(() => {
          this.setBusy(btn, false);
        });
    }

    setBusy(btn, busy) {
      if (!btn) return;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      } else {
        btn.setAttribute('aria-busy', 'false');
        btn.disabled = false;
      }
    }

    announce(msg) {
      let live = this.querySelector('[data-ck-live]');
      if (!live) {
        live = document.createElement('p');
        live.className = 'visually-hidden';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('data-ck-live', '');
        this.appendChild(live);
      }
      live.textContent = '';
      void live.offsetWidth;
      live.textContent = msg;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('ck-product-upsell', CkProductUpsell);
})();
